import { forwardRef, useState, useRef, useEffect, useMemo, createContext, useContext } from 'react';
import { useRouter } from 'next/router';
import { Disclosure } from '@headlessui/react';
import { IoIosArrowDown } from 'react-icons/io';
import { BsFillArrowRightCircleFill, BsFillArrowLeftCircleFill } from 'react-icons/bs';
import { IoListCircle } from 'react-icons/io5';
import { IoIosList } from 'react-icons/io';
import Image from 'next/image';
import FileIcon from '@assets/bytes/file-icon.svg';
import cn from 'classnames';

const ModuleLessonRouteCtx = createContext();

const ModuleLessonRoute = forwardRef(
  ({ moduleLessons, moduleList, ProgressWidth, decoupled = false, children }, ref) => {
    const [moduleOpen, setModuleOpen] = useState(false);
    const [lessonOpen, setLessonOpen] = useState(false);
    const router = useRouter();
    const { lesson, slug } = router.query;

    const moduleDropdownRef = useRef(null);
    const lessonDropdownRef = useRef(null);
    const nextButton = useRef();
    const prevButton = useRef();

    const moduleKey = useMemo(
      () =>
        Object.keys(moduleLessons).find((key) =>
          moduleLessons[key].some((less) => less.slug === lesson)
        ),
      [lesson]
    );

    const moduleIndex = useMemo(
      () => moduleList?.findIndex?.((item) => item.name === moduleKey),
      [lesson]
    );

    const lessonIndex = useMemo(
      () => moduleLessons[moduleKey]?.findIndex?.((item) => item.slug === lesson),
      [lesson]
    );

    const [selectedModule, setSelectedModule] = useState(moduleList[moduleIndex]);
    const [selectedLesson, setSelectedLesson] = useState(
      moduleLessons[selectedModule?.name]?.[lessonIndex]
    );

    // const handleModuleOptionChange = (option) => {
    //   setSelectedLesson(moduleLessons[option.name][0]);
    //   router.push(`/bytes/tutorials/${slug}/${moduleLessons[option.name][0].slug}`);
    // };

    const handleLessonOptionChange = (option, module) => {
      setSelectedLesson(option);
      setSelectedModule(module);
      setModuleOpen(false);
      router.push(`/bytes/tutorials/${slug}/${option.slug}`);
    };

    const handlePrevLesson = () => {
      const prevIndex =
        moduleLessons?.[selectedModule?.name]?.findIndex?.(
          (lesson) => lesson.title === selectedLesson.title
        ) - 1;
      if (prevIndex >= 0) {
        setSelectedModule(selectedModule);
        setSelectedLesson(moduleLessons?.[selectedModule?.name][prevIndex]);
        router.push(
          `/bytes/tutorials/${slug}/${moduleLessons?.[selectedModule?.name][prevIndex].slug}`
        );
      } else {
        if (moduleLessons?.[selectedModule?.name]?.[0].title === selectedLesson.title) {
          if (moduleList[moduleIndex - 1]) {
            setSelectedModule(moduleList[moduleIndex - 1]);
            setSelectedLesson(moduleLessons?.[moduleList[moduleIndex - 1].name]?.[0]);
            router.push(
              `/bytes/tutorials/${slug}/${
                moduleLessons?.[moduleList[moduleIndex - 1].name]?.[0]?.slug
              }`
            );
            return;
          }
        }
      }
    };

    const handleNextLesson = () => {
      if (!Boolean(moduleLessons?.[selectedModule?.name]?.length - 1 > lessonIndex)) {
        const nextModule = moduleList?.[moduleIndex + 1];
        if (nextModule) {
          const nextLesson = moduleLessons?.[nextModule.name]?.[0];
          setSelectedLesson(nextLesson);
          setSelectedModule(nextModule);
          router.push(`/bytes/tutorials/${slug}/${nextLesson.slug}`);
          return;
        }
      }
      const nextIndex =
        moduleLessons?.[selectedModule?.name]?.findIndex?.(
          (lesson) => lesson.title === selectedLesson.title
        ) + 1;
      if (nextIndex < moduleLessons[selectedModule?.name]?.length) {
        setSelectedLesson(moduleLessons?.[selectedModule?.name][nextIndex]);
        router.push(
          `/bytes/tutorials/${slug}/${moduleLessons?.[selectedModule?.name][nextIndex].slug}`
        );
      }
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(event.target)) {
          setModuleOpen(false);
        }
        if (lessonDropdownRef.current && !lessonDropdownRef.current.contains(event.target)) {
          setLessonOpen(false);
        }
      };

      if (moduleOpen || lessonOpen) {
        window.addEventListener('click', handleClickOutside);
      }

      return () => {
        window.removeEventListener('click', handleClickOutside);
      };
    }, [moduleOpen, lessonOpen, selectedLesson]);

    if (decoupled && !selectedLesson && !selectedModule) return <></>;

    return (
      <ModuleLessonRouteCtx.Provider
        value={{
          moduleLessons,
          moduleList,
          moduleIndex,
          lessonIndex,
          ref,
          selectedModule,
          selectedLesson,
          nextButton,
          prevButton,
          handleNextLesson,
          handlePrevLesson,
          handleLessonOptionChange,
          moduleOpen,
          setModuleOpen,
          moduleDropdownRef,
        }}
      >
        {children}
      </ModuleLessonRouteCtx.Provider>
    );
  }
);

ModuleLessonRoute.displayName = 'ModuleLessonRoute';
export default ModuleLessonRoute;

export const useModuleLessonRouteCtx = () => {
  return useContext(ModuleLessonRouteCtx);
};

export function CourseOutlineBox({ boxStyle }) {
  const { moduleLessons, moduleList, selectedModule, selectedLesson, handleLessonOptionChange } =
    useModuleLessonRouteCtx();

  return (
    <div
      style={{ boxShadow: '4px 0px 24px 0px rgba(0, 0, 0, 0.05)' }}
      className={cn(
        'w-full overflow-hidden border-t border-r border-[#F2F4F7] bg-white md:rounded-r-lg',
        boxStyle
      )}
    >
      <div className="flex flex-col gap-y-[24px] p-[24px]">
        <p className="flex items-center gap-x-[8px] font-satoshi-bold text-base text-[16px] leading-7 text-red">
          <IoIosList size={24} /> Course Outline
        </p>

        <hr className="text-[#F2F4F7]" />

        <div className="slim-scroll flex h-fit max-h-[60vh] flex-col items-start gap-y-[24px] bg-white md:max-h-[40vh]">
          {moduleList?.map((module, index) => (
            <Disclosure
              as="div"
              key={module?.id}
              className={'flex w-full flex-col items-start gap-y-[12px]'}
              defaultOpen={selectedModule.name === module.name}
            >
              {({ open }) => (
                <>
                  <Disclosure.Button
                    className={'flex w-full items-center justify-between text-[#505050]'}
                  >
                    <span
                      className={cn(
                        'flex w-4/5 flex-col gap-y-[2px] text-left font-satoshi-medium font-satoshi-medium text-[16px] font-medium  capitalize leading-6',
                        { 'font-satoshi-bold text-gray-900': selectedModule.name === module.name }
                      )}
                    >
                      <span className="text-[12px] leading-4 text-gray-navigation">
                        Module {index + 1}
                      </span>
                      {module.name}
                    </span>
                    <IoIosArrowDown
                      className={`${open ? 'rotate-180 transform' : ''} w-1/5`}
                      size={24}
                    />
                  </Disclosure.Button>
                  {moduleLessons?.[module?.name]?.map((option) => (
                    <Disclosure.Panel
                      key={option.title}
                      className={`${
                        option.title === selectedLesson.title ? 'text-red' : 'text-gray-400'
                      } flex cursor-pointer items-center gap-x-[4px] font-satoshi-medium font-satoshi-medium text-[14px] font-medium  capitalize leading-5`}
                      onClick={() => handleLessonOptionChange(option, module)}
                    >
                      <Image
                        src={FileIcon}
                        height={24}
                        width={24}
                        objectFit="contain"
                        alt="file-icon"
                      />

                      {option.title}
                    </Disclosure.Panel>
                  ))}
                </>
              )}
            </Disclosure>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BottomBar({ className, children }) {
  const {
    moduleLessons,
    moduleList,
    moduleIndex,
    lessonIndex,
    ref,
    selectedModule,
    selectedLesson,
    nextButton,
    prevButton,
    handleNextLesson,
    handlePrevLesson,
    moduleOpen,
    setModuleOpen,
    moduleDropdownRef,
  } = useModuleLessonRouteCtx();

  return (
    <>
      {moduleOpen && children && (
        <div className="fixed inset-0 w-full bg-black/10 backdrop-blur-[1px] lg:hidden" />
      )}
      <div className="relative w-full" ref={moduleDropdownRef}>
        <div
          ref={ref}
          className={cn(
            'grid w-full grid-cols-12 items-center border-t border-[#F2F4F7] bg-white px-[16px] pt-[11px] pb-[20px] md:px-[64px]',
            className
          )}
        >
          <button
            ref={prevButton}
            disabled={
              moduleLessons?.[selectedModule?.name]?.[0].title === selectedLesson.title &&
              !moduleList[moduleIndex - 1]
            }
            className="col-span-2 disabled:pointer-events-none disabled:opacity-30 md:col-span-1 md:place-self-center"
          >
            <BsFillArrowLeftCircleFill
              className="h-8 w-8 rounded-full bg-red text-white"
              onClick={handlePrevLesson}
              size={30}
            />
          </button>

          <div className="col-span-8 flex items-center gap-4 md:col-span-10">
            <div
              className="grid cursor-pointer grid-cols-12 items-center md:gap-x-[12px]"
              onClick={() => setModuleOpen && setModuleOpen(!moduleOpen)}
            >
              <IoListCircle size={40} className="col-span-2 text-red" />
              <div className="col-span-10 flex flex-col gap-y-[4px]">
                <span className="font-satoshi-medium text-[16px] capitalize leading-6 line-clamp-1">
                  Module {selectedModule.id}: {selectedModule.name}
                </span>

                <span className=" font-satoshi-medium text-[14px] leading-5 text-gray-navigation line-clamp-1">
                  <span className="hidden lg:inline-flex">
                    Lesson {selectedLesson?.serialNo}:&nbsp;
                  </span>
                  {selectedLesson.title}
                </span>
              </div>
            </div>
          </div>

          <button
            ref={nextButton}
            disabled={
              !Boolean(moduleLessons?.[selectedModule?.name]?.length - 1 > lessonIndex) &&
              !moduleList?.[moduleIndex + 1]
            }
            className="col-span-2 place-self-end self-center disabled:pointer-events-none disabled:opacity-30	md:col-span-1"
          >
            <BsFillArrowRightCircleFill
              className="h-8 w-8 rounded-full bg-red text-white"
              onClick={handleNextLesson}
            />
          </button>
        </div>

        {moduleOpen && children}
      </div>
    </>
  );
}
